import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import {
  TOWN_PORTAL_LINK_PERMISSIONS,
  applyLinkPermissionChange,
} from './townPortalLinkPermissions.js';

const colors = {
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
};

const PortalSwitch = ({ checked, onChange, disabled }) => (
  <Switch
    size="small"
    checked={checked}
    onChange={onChange}
    disabled={disabled}
    sx={{
      width: 32,
      height: 18,
      p: 0,
      flexShrink: 0,
      mt: '1px',
      '& .MuiSwitch-switchBase': {
        p: 0,
        m: '3px',
        transitionDuration: '150ms',
        '&.Mui-checked': {
          transform: 'translateX(14px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            backgroundColor: colors.accent,
            opacity: 1,
          },
        },
      },
      '& .MuiSwitch-thumb': { width: 12, height: 12 },
      '& .MuiSwitch-track': {
        borderRadius: 9,
        backgroundColor: colors.borderSecondary,
        opacity: 1,
      },
    }}
  />
);

export const ToggleRow = ({ name, description, checked, onChange, disabled }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      py: 1,
      borderBottom: `0.5px solid ${colors.borderTertiary}`,
      '&:last-child': { borderBottom: 'none', pb: 0 },
      '&:first-of-type': { pt: 0 },
    }}
  >
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>
        {name}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
          {description}
        </Typography>
      )}
    </Box>
    <PortalSwitch checked={checked} onChange={onChange} disabled={disabled} />
  </Box>
);

/**
 * Portal link permission toggles driven by {@link TOWN_PORTAL_LINK_PERMISSIONS}.
 */
const LinkPermissionOptions = ({ permissionState, onPermissionChange }) => {
  const setPerm = (apiKey, value) => {
    onPermissionChange(applyLinkPermissionChange(permissionState, apiKey, value));
  };

  const parentEnabled = (parentApiKey) =>
    parentApiKey ? Boolean(permissionState[parentApiKey]) : true;

  return (
    <>
      {TOWN_PORTAL_LINK_PERMISSIONS.map((perm) => {
        if (perm.dialogKind === 'childToggle') {
          const parentOn = parentEnabled(perm.parentApiKey);
          return (
            <Box
              key={perm.apiKey}
              sx={{
                ml: 2.75,
                pt: 0.75,
                opacity: parentOn ? 1 : 0.5,
                pointerEvents: parentOn ? 'auto' : 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                <SubdirectoryArrowRightIcon
                  sx={{
                    fontSize: 13,
                    color: parentOn ? colors.textTertiary : colors.borderSecondary,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    color: parentOn ? colors.textSecondary : colors.textTertiary,
                    flex: 1,
                  }}
                >
                  {perm.dialogLabel}
                </Typography>
                <PortalSwitch
                  checked={parentOn && Boolean(permissionState[perm.apiKey])}
                  onChange={(e) => setPerm(perm.apiKey, e.target.checked)}
                  disabled={!parentOn}
                />
              </Box>
            </Box>
          );
        }

        if (perm.dialogKind !== 'toggle') return null;

        return (
          <ToggleRow
            key={perm.apiKey}
            name={perm.dialogLabel}
            description={perm.dialogDescription}
            checked={Boolean(permissionState[perm.apiKey])}
            onChange={(e) => setPerm(perm.apiKey, e.target.checked)}
          />
        );
      })}
    </>
  );
};

export default LinkPermissionOptions;
